import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    function handleScroll() {
      const nav = document.getElementById('main-nav');
      if (nav) {
        if (window.scrollY > 50) {
          nav.classList.add('bg-background-50/95', 'backdrop-blur-md');
          nav.classList.remove('bg-transparent');
        } else {
          nav.classList.add('bg-transparent');
          nav.classList.remove('bg-background-50/95', 'backdrop-blur-md');
        }
      }
    }
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const articleLinks = [
    { name: 'Complete Guide', path: '/article/superbuy-spreadsheet-complete-guide', icon: 'ri-book-open-line' },
    { name: 'Shipping Optimization', path: '/article/superbuy-spreadsheet-shipping-guide', icon: 'ri-ship-line' },
    { name: 'Beginner\'s Guide', path: '/article/superbuy-spreadsheet-for-beginners', icon: 'ri-seedling-line' },
    { name: 'Template Library', path: '/article/superbuy-spreadsheet-template-guide', icon: 'ri-file-copy-line' },
  ];

  const toggleMobileSection = (section: string) => {
    setMobileExpandedSection(mobileExpandedSection === section ? null : section);
  };

  return (
    <nav
      id="main-nav"
      className="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-transparent"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link
          to="/"
          className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-200"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center">
            <i className="ri-store-2-line text-xl md:text-2xl"></i>
          </div>
          <span className="font-heading text-lg md:text-xl font-semibold whitespace-nowrap">
            SuperbuySheet
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors duration-200 rounded-lg hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              <span>Categories</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-96 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/60 transition-colors duration-150 whitespace-nowrap"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-base`}></i>
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer relative z-50"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <div className="relative w-5 h-4">
            <span className={`absolute left-0 w-full h-0.5 bg-foreground-800 rounded-full transition-all duration-300 ${mobileMenuOpen ? 'top-1.5 rotate-45' : 'top-0'}`}></span>
            <span className={`absolute left-0 w-full h-0.5 bg-foreground-800 rounded-full transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : 'top-1.5'}`}></span>
            <span className={`absolute left-0 w-full h-0.5 bg-foreground-800 rounded-full transition-all duration-300 ${mobileMenuOpen ? 'top-1.5 -rotate-45' : 'top-3'}`}></span>
          </div>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-6 space-y-6">
            <div>
              <button
                onClick={() => toggleMobileSection('categories')}
                className="w-full flex items-center justify-between py-3 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-grid-line text-primary-600 text-lg"></i>
                  </div>
                  <span className="text-base font-semibold text-foreground-900">Shop Categories</span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileExpandedSection === 'categories' ? 'rotate-180' : ''}`}></i>
                </div>
              </button>
              {mobileExpandedSection === 'categories' && (
                <div className="ml-12 mt-1 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50/50 rounded-lg transition-colors duration-150"
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/70 pt-1">
              <button
                onClick={() => toggleMobileSection('guides')}
                className="w-full flex items-center justify-between py-3 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-article-line text-accent-600 text-lg"></i>
                  </div>
                  <span className="text-base font-semibold text-foreground-900">SEO Guides</span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileExpandedSection === 'guides' ? 'rotate-180' : ''}`}></i>
                </div>
              </button>
              {mobileExpandedSection === 'guides' && (
                <div className="ml-12 mt-1 space-y-0.5">
                  {articleLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:text-accent-600 hover:bg-accent-50/50 rounded-lg transition-colors duration-150"
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${link.icon} text-sm`}></i>
                      </div>
                      <span className="whitespace-nowrap">{link.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/70 pt-6 pb-4 space-y-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-3 w-full px-5 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-bag-line"></i>
                </div>
                <span>Buy It Now</span>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-3 w-full px-5 py-3 border-2 border-foreground-200 text-foreground-800 text-sm font-semibold rounded-full hover:border-foreground-300 hover:bg-foreground-50 transition-all duration-200 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-store-2-line"></i>
                </div>
                <span>Superbuy Sheet</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}